(() => {
  const CLIENT_MODULE_URL = 'https://esm.sh/@vercel/blob@2.3.2/client?bundle&target=es2022';
  const MULTIPART_THRESHOLD_BYTES = 8 * 1024 * 1024;
  const DIRECT_AUDIO_EXTENSIONS = new Set(['mp3', 'm4a', 'aac', 'wav', 'flac', 'ogg']);
  const DIRECT_VIDEO_EXTENSIONS = new Set(['mp4', 'm4v', 'mov', 'webm']);

  let blobClientPromise;

  function getBlobClient() {
    blobClientPromise = blobClientPromise || import(CLIENT_MODULE_URL);
    return blobClientPromise;
  }

  function formatFileSize(bytes) {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  function formatDuration(minutes) {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
    }

    return `${minutes} min`;
  }

  function sanitizeFileName(fileName) {
    return (
      String(fileName || '')
        .trim()
        .replace(/[/\\]/g, '-')
        .replace(/[^a-zA-Z0-9._ -]+/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^\.+/, '')
        .slice(0, 140) || 'uploaded-media'
    );
  }

  function setStatus(root, message, tone) {
    const status = root.querySelector('[data-whisper-blob-status]');
    if (!status) return;

    status.textContent = message;
    if (tone) {
      status.dataset.tone = tone;
    } else {
      delete status.dataset.tone;
    }
  }

  function setProgress(root, percentage, visible = true) {
    const progress = root.querySelector('[data-whisper-blob-progress]');
    if (!progress) return;

    progress.value = percentage;
    progress.dataset.visible = visible ? 'true' : 'false';
  }

  function setHiddenValue(root, selector, value) {
    const input = root.querySelector(selector);
    if (input) input.value = value || '';
  }

  function clearQuantityFallback(root) {
    const hiddenQuantity = root.querySelector('[data-whisper-hidden-quantity]');
    if (!hiddenQuantity) return;

    hiddenQuantity.removeAttribute('name');
    hiddenQuantity.value = '';
  }

  function findQuantityInput(form, root) {
    const formQuantity = form.querySelector('[name="quantity"]');
    if (formQuantity) return formQuantity;

    const formId = form.getAttribute('id');
    if (formId) {
      const externalQuantity = document.querySelector(`input[name="quantity"][form="${formId}"]`);
      if (externalQuantity) return externalQuantity;
    }

    return root.querySelector('[data-whisper-hidden-quantity]');
  }

  function setCartQuantity(form, root, units) {
    const safeUnits = Math.max(1, Number(units) || 1);
    const quantityInput = findQuantityInput(form, root);
    if (!quantityInput) return;

    if (quantityInput.matches('[data-whisper-hidden-quantity]')) {
      quantityInput.name = 'quantity';
    } else {
      clearQuantityFallback(root);
    }

    quantityInput.value = String(safeUnits);
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function getBillingUnitMinutes(root) {
    return Math.max(1, Number(root.dataset.billingUnitMinutes || 30) || 30);
  }

  function getBillingUnits(root, durationSeconds) {
    const durationMinutes = Math.max(1, Math.ceil(durationSeconds / 60));
    const billingUnits = Math.max(1, Math.ceil(durationMinutes / getBillingUnitMinutes(root)));
    return { durationMinutes, billingUnits };
  }

  function setDetectedDuration(form, root, durationSeconds, title) {
    const { durationMinutes, billingUnits } = getBillingUnits(root, durationSeconds);

    setHiddenValue(root, '[data-whisper-duration-minutes]', String(durationMinutes));
    setHiddenValue(root, '[data-whisper-billing-units]', String(billingUnits));
    if (title) setHiddenValue(root, '[data-whisper-media-title]', title);
    setCartQuantity(form, root, billingUnits);

    return { durationMinutes, billingUnits };
  }

  function resetUploadState(root) {
    setHiddenValue(root, '[data-whisper-source-type]', '');
    setHiddenValue(root, '[data-whisper-source-url]', '');
    setHiddenValue(root, '[data-whisper-media-title]', '');
    setHiddenValue(root, '[data-whisper-duration-minutes]', '');
    setHiddenValue(root, '[data-whisper-billing-units]', '');
    setHiddenValue(root, '[data-whisper-blob-url]', '');
    setHiddenValue(root, '[data-whisper-blob-name]', '');
    setHiddenValue(root, '[data-whisper-blob-size]', '');
    setHiddenValue(root, '[data-whisper-blob-type]', '');
    setProgress(root, 0, false);
    clearQuantityFallback(root);
    delete root.dataset.uploadedFileKey;
    delete root.dataset.checkedLinkKey;
  }

  function getSelectedMode(root) {
    return root.querySelector('[data-whisper-source-mode]:checked')?.value || 'upload';
  }

  function getSelectedFile(root) {
    const input = root.querySelector('[data-whisper-blob-file]');
    return input && input.files ? input.files[0] : null;
  }

  function getCurrentVariantId(form, root) {
    const variantInput = form.querySelector('[name="id"]');
    return variantInput && variantInput.value ? variantInput.value : root.dataset.variantId || '';
  }

  function buildUploadPath(root, file) {
    const productHandle = root.dataset.productHandle || 'transcription';
    const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `whisperaiml/product-media/${productHandle}/${Date.now()}-${id}-${sanitizeFileName(file.name)}`;
  }

  function readMediaDuration(source, kind) {
    return new Promise((resolve, reject) => {
      const media = kind === 'audio' ? new Audio() : document.createElement('video');
      let objectUrl;
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('Could not read media duration before timeout.'));
      }, 15000);

      function cleanup() {
        window.clearTimeout(timeout);
        media.removeAttribute('src');
        media.load();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }

      media.preload = 'metadata';
      media.onloadedmetadata = () => {
        const duration = media.duration;
        cleanup();
        if (Number.isFinite(duration) && duration > 0) {
          resolve(Math.round(duration));
        } else {
          reject(new Error('Could not read media duration.'));
        }
      };
      media.onerror = () => {
        cleanup();
        reject(new Error('Could not read media duration.'));
      };

      if (source instanceof File) {
        objectUrl = URL.createObjectURL(source);
        media.src = objectUrl;
      } else {
        media.src = source;
      }
    });
  }

  function getDirectLinkKind(url) {
    let extension = '';
    try {
      const parsedUrl = new URL(url);
      extension = (parsedUrl.pathname.split('.').pop() || '').toLowerCase();
    } catch {
      return null;
    }

    if (DIRECT_AUDIO_EXTENSIONS.has(extension)) return 'audio';
    if (DIRECT_VIDEO_EXTENSIONS.has(extension)) return 'video';
    return null;
  }

  async function detectFileDuration(file) {
    if (!file) return null;
    if (file.type.startsWith('audio/')) return readMediaDuration(file, 'audio');
    if (file.type.startsWith('video/')) return readMediaDuration(file, 'video');
    return null;
  }

  async function checkMediaLink(root, form) {
    const input = root.querySelector('[data-whisper-media-link]');
    const link = input ? input.value.trim() : '';

    if (!link) {
      throw new Error('Paste an approved media link first.');
    }

    setStatus(root, 'Checking approved media link duration...', null);
    setProgress(root, 0, false);

    const response = await fetch(root.dataset.linkCheckUrl, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ url: link }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Media link could not be checked.');
    }

    let durationSeconds = Number(data.durationSeconds || 0);
    if (!durationSeconds && data.needsClientDuration) {
      const kind = getDirectLinkKind(link);
      if (!kind) {
        throw new Error('This direct media link type is not approved.');
      }
      durationSeconds = await readMediaDuration(link, kind);
    }

    if (!durationSeconds) {
      throw new Error('Could not detect duration for this link. Upload the media file instead.');
    }

    setHiddenValue(root, '[data-whisper-source-type]', `link:${data.provider || 'approved'}`);
    setHiddenValue(root, '[data-whisper-source-url]', link);
    setHiddenValue(root, '[data-whisper-media-title]', data.title || link);
    setHiddenValue(root, '[data-whisper-blob-url]', '');
    setHiddenValue(root, '[data-whisper-blob-name]', '');
    setHiddenValue(root, '[data-whisper-blob-size]', '');
    setHiddenValue(root, '[data-whisper-blob-type]', '');

    const { durationMinutes, billingUnits } = setDetectedDuration(form, root, durationSeconds, data.title || link);
    root.dataset.checkedLinkKey = link;
    setStatus(
      root,
      `Link approved: ${formatDuration(durationMinutes)} detected. Quantity set to ${billingUnits}.`,
      'success'
    );

    return data;
  }

  async function prepareLinkSource(form, root) {
    const input = root.querySelector('[data-whisper-media-link]');
    const link = input ? input.value.trim() : '';
    const billingUnits = root.querySelector('[data-whisper-billing-units]')?.value;

    if (link && root.dataset.checkedLinkKey === link && billingUnits) {
      setCartQuantity(form, root, Number(billingUnits));
      return;
    }

    await checkMediaLink(root, form);
  }

  async function prepareUploadSource(form, root) {
    const file = getSelectedFile(root);
    const uploadedUrl = root.querySelector('[data-whisper-blob-url]')?.value;
    const fileKey = file ? `${file.name}:${file.size}:${file.lastModified}` : '';

    if (uploadedUrl && root.dataset.uploadedFileKey === fileKey) {
      const billingUnits = root.querySelector('[data-whisper-billing-units]')?.value;
      if (billingUnits) setCartQuantity(form, root, Number(billingUnits));
      return;
    }

    if (!file) {
      throw new Error('Upload your media file before adding this transcription product to cart.');
    }

    const maxSizeBytes = Number(root.dataset.maxSizeBytes || 0);
    if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
      throw new Error(`This file is too large. The limit is ${formatFileSize(maxSizeBytes)}.`);
    }

    const handleUploadUrl = root.dataset.handleUploadUrl;
    if (!handleUploadUrl) {
      throw new Error('Upload service is not configured.');
    }

    setHiddenValue(root, '[data-whisper-source-type]', 'upload');
    setHiddenValue(root, '[data-whisper-source-url]', '');
    setHiddenValue(root, '[data-whisper-media-title]', file.name);

    try {
      const durationSeconds = await detectFileDuration(file);
      if (durationSeconds) {
        const { durationMinutes, billingUnits } = setDetectedDuration(form, root, durationSeconds, file.name);
        setStatus(root, `${formatDuration(durationMinutes)} detected. Quantity set to ${billingUnits}. Uploading file...`, null);
      } else {
        setStatus(root, 'Uploading media to Whisper AiML storage...', null);
      }
    } catch {
      setStatus(root, 'Uploading media to Whisper AiML storage. Duration could not be detected, so quantity will stay as entered.', null);
    }

    setProgress(root, 1, true);

    const { upload } = await getBlobClient();
    const blob = await upload(buildUploadPath(root, file), file, {
      access: 'public',
      contentType: file.type || undefined,
      handleUploadUrl,
      multipart: file.size > MULTIPART_THRESHOLD_BYTES,
      clientPayload: JSON.stringify({
        productHandle: root.dataset.productHandle || '',
        productId: root.dataset.productId || '',
        variantId: getCurrentVariantId(form, root),
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || 'application/octet-stream',
        source: 'whisperaiml-product-page',
      }),
      onUploadProgress: ({ percentage }) => {
        setProgress(root, Math.max(1, Math.round(percentage)), true);
      },
    });

    setHiddenValue(root, '[data-whisper-source-url]', blob.url);
    setHiddenValue(root, '[data-whisper-blob-url]', blob.url);
    setHiddenValue(root, '[data-whisper-blob-name]', file.name);
    setHiddenValue(root, '[data-whisper-blob-size]', String(file.size));
    setHiddenValue(root, '[data-whisper-blob-type]', file.type || 'application/octet-stream');
    root.dataset.uploadedFileKey = fileKey;
    setProgress(root, 100, true);
    setStatus(root, 'Upload complete. Continue to checkout when ready.', 'success');
  }

  async function prepareFormUpload(form) {
    const root = form.querySelector('[data-whisper-blob-upload]');
    if (!root) return;

    if (getSelectedMode(root) === 'link') {
      await prepareLinkSource(form, root);
      return;
    }

    await prepareUploadSource(form, root);
  }

  function syncMode(root) {
    const mode = getSelectedMode(root);
    const uploadPanel = root.querySelector('[data-whisper-upload-panel]');
    const linkPanel = root.querySelector('[data-whisper-link-panel]');
    if (uploadPanel) uploadPanel.hidden = mode !== 'upload';
    if (linkPanel) linkPanel.hidden = mode !== 'link';

    resetUploadState(root);
    setStatus(
      root,
      mode === 'link'
        ? 'Paste an approved media link and check duration before adding to cart.'
        : 'Choose a file before adding this transcription product to cart.',
      null
    );
  }

  function initUploader(root) {
    if (root.dataset.whisperUploaderInitialized === 'true') return;
    root.dataset.whisperUploaderInitialized = 'true';

    const form = root.closest('form');
    const input = root.querySelector('[data-whisper-blob-file]');
    const fileName = root.querySelector('[data-whisper-blob-file-name]');
    const linkInput = root.querySelector('[data-whisper-media-link]');
    const checkButton = root.querySelector('[data-whisper-check-link]');

    root.querySelectorAll('[data-whisper-source-mode]').forEach((radio) => {
      radio.addEventListener('change', () => syncMode(root));
    });

    if (input) {
      input.addEventListener('change', async () => {
        resetUploadState(root);
        const file = getSelectedFile(root);
        if (fileName) {
          fileName.textContent = file ? `${file.name} (${formatFileSize(file.size)})` : 'No file selected';
        }
        setStatus(
          root,
          file ? 'File selected. Upload will start when you add this product to cart.' : 'Choose a file before adding this transcription product to cart.',
          null
        );
      });
    }

    if (linkInput) {
      linkInput.addEventListener('input', () => {
        resetUploadState(root);
        setStatus(root, 'Paste an approved media link and check duration before adding to cart.', null);
      });
    }

    if (checkButton && form) {
      checkButton.addEventListener('click', async () => {
        checkButton.disabled = true;
        try {
          await checkMediaLink(root, form);
        } catch (error) {
          console.error(error);
          setStatus(root, error instanceof Error ? error.message : 'Media link could not be checked.', 'error');
        } finally {
          checkButton.disabled = false;
        }
      });
    }

    syncMode(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-whisper-blob-upload]').forEach(initUploader);
  });

  window.WhisperMediaUpload = {
    prepareFormUpload,
  };
})();
